<script setup lang="ts">
import type { FormKitFrameworkContext, FormKitNode } from '@formkit/core'
import {
  TranslatableEntityType,
  type TranslatedField,
  type TranslationResult,
} from '~/graphql/operations'
import AiFillButton from '~/components/translation/AiFillButton.vue'

const props = defineProps<{ context: FormKitFrameworkContext }>()

const attrProps = computed<
  {
    entityType: TranslatableEntityType
    entityId?: number | null
    sourceLanguageId?: number | null
    field: string
    sourceChannelId?: number | null
    targetChannelId?: number | null
  } & AiFillCollectionOptions
>(() => (props.context.attrs?.props as any) || {})

const groupNode = computed<FormKitNode | undefined>(
  () => props.context.node.parent || undefined,
)

const targetLanguageId = computed<number | null>(() => {
  const value: any = groupNode.value?.value
  const id = value?.languageId
  return typeof id === 'number' ? id : id ? +id : null
})

const isTargetTab = computed(
  () =>
    !!attrProps.value.sourceLanguageId &&
    !!targetLanguageId.value &&
    attrProps.value.sourceLanguageId !== targetLanguageId.value,
)

// Child collection texts (e.g. module images) are requested as `image:42:name`.
const isCollection = computed(() => !!attrProps.value.collectionKey)
const collectionChildId = useCollectionChildId(props.context.node, attrProps)
const requestedFieldName = computed<string | null>(() => {
  if (!isCollection.value) return attrProps.value.field
  if (!collectionChildId.value) return null
  return encodeCollectionField(
    attrProps.value.collectionKey!,
    collectionChildId.value,
    attrProps.value.field,
  )
})

const findChildByName = (
  node: FormKitNode,
  name: string,
): FormKitNode | undefined => {
  const direct = node.children.find(
    (c: any) => !('__FKP' in c) && String(c.name) === name,
  )
  if (direct) return direct as FormKitNode
  for (const c of node.children as any[]) {
    if ('__FKP' in c) continue
    const nested = (c.children || []).find(
      (cc: any) => !('__FKP' in cc) && String(cc.name) === name,
    )
    if (nested) return nested
  }
  return undefined
}

const onFilled = (payload: {
  translatedFields: TranslatedField[]
  result: TranslationResult
}) => {
  const node = groupNode.value
  if (!node || !requestedFieldName.value) return
  const match = payload.translatedFields.find(
    (f) => f.name === requestedFieldName.value,
  )
  if (match?.value != null) {
    const child = findChildByName(node, attrProps.value.field)
    if (child) child.input(match.value, false)
  }
}
</script>

<template>
  <div
    v-if="
      isTargetTab && attrProps.entityId && targetLanguageId && requestedFieldName
    "
    class="ai-fill-field inline-flex"
  >
    <AiFillButton
      :entity-type="attrProps.entityType || TranslatableEntityType.Module"
      :entity-id="attrProps.entityId"
      :target-language-id="targetLanguageId"
      :source-language-id="attrProps.sourceLanguageId ?? null"
      :source-channel-id="attrProps.sourceChannelId ?? null"
      :target-channel-id="attrProps.targetChannelId ?? null"
      :fields="[requestedFieldName]"
      :persist-key="`translation:sourceLang:${attrProps.entityType}:${attrProps.entityId ?? 'new'}:${requestedFieldName}`"
      compact
      @filled="onFilled"
    />
  </div>
</template>
